import React from 'react';

import { MenuItem, Select } from '@mui/material';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';

import { definitions } from '@luxuryescapes/contract-svc-promo';

export { RewardFilterEditCell } from './RewardFilterEditCell';
export { EditableTextField as TextEditCell } from '../Accommodation/utils/datagrid';

const CONDITION_APPLY_TYPES = ['NONE', 'MUST-INCLUDE', 'MUST-ONLY-INCLUDE', 'MUST-EXCLUDE'] as const;
export type ConditionApplyType = (typeof CONDITION_APPLY_TYPES)[number];

const VOUCHER_CONDITION_TYPES: ReadonlyArray<definitions['VoucherConditionV1']['condition_type']> = [
  'ITEM-DESTINATION-VALIDATION-V2',
  'LOYALTY-OFFER-TYPE-VALIDATION',
  'ITEM-MARGIN-VALIDATION',
  'OFFER-ID-VALIDATION',
  'ARRIVAL-AIRPORT-CODE-VALIDATION',
  'ITEM-PROVIDER-VALIDATION',
  'ORIGIN-AIRPORT-CODE-VALIDATION',
  'NUMBER-OF-CHILDREN-VALIDATION-V2',
  'NUMBER-OF-PEOPLE-VALIDATION-V2',
  'NUMBER-OF-ADULTS-VALIDATION-V2',
  'SUBSCRIBER-TIER-VALIDATION',
] as const;

export function formatConditionLabel(value: string) {
  return value
    .split('-')
    .map((part) => {
      if (part.toUpperCase() === 'V2') {
        return 'V2';
      }
      return part.charAt(0) + part.slice(1).toLowerCase();
    })
    .join(' ');
}

export function formatApplyTypeLabel(value: string) {
  return value
    .split('-')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

type ConditionTypeEditCellProps = GridRenderEditCellParams & {
  conditionTypes?: ReadonlyArray<definitions['VoucherConditionV1']['condition_type']>;
  /** Fires when the user picks a new condition type while the cell is in edit mode (before row save). */
  onConditionTypeChange?: (nextType: string) => void;
};

export function ConditionTypeEditCell({
  id,
  field,
  value,
  conditionTypes,
  onConditionTypeChange,
}: ConditionTypeEditCellProps) {
  const apiRef = useGridApiContext();
  const types = conditionTypes && conditionTypes.length > 0 ? conditionTypes : VOUCHER_CONDITION_TYPES;
  return (
    <Select
      value={value || ''}
      onChange={(event) => {
        const newValue = event.target.value;
        apiRef.current.setEditCellValue({ id, field, value: newValue });
        // Sync the details column so it re-renders with the new condition type
        apiRef.current.setEditCellValue({ id, field: 'condition_details', value: newValue });
        onConditionTypeChange?.(newValue);
      }}
      size="small"
      fullWidth
    >
      {types.map((type) => (
        <MenuItem key={type} value={type}>
          {formatConditionLabel(type)}
        </MenuItem>
      ))}
    </Select>
  );
}

type ApplyTypeEditCellProps = GridRenderEditCellParams & {
  applyTypes?: ReadonlyArray<ConditionApplyType>;
};

export function ApplyTypeEditCell({ id, field, value, applyTypes }: ApplyTypeEditCellProps) {
  const apiRef = useGridApiContext();
  const types = applyTypes && applyTypes.length > 0 ? applyTypes : CONDITION_APPLY_TYPES;
  const fallback = types[0] ?? 'NONE';
  const selectedValue = types.includes(value as ConditionApplyType) ? value : fallback;
  return (
    <Select
      value={selectedValue || fallback}
      onChange={(event) => apiRef.current.setEditCellValue({ id, field, value: event.target.value })}
      size="small"
      fullWidth
    >
      {types.map((type) => (
        <MenuItem key={type} value={type}>
          {formatApplyTypeLabel(type)}
        </MenuItem>
      ))}
    </Select>
  );
}

export const voucherConditionTypes = VOUCHER_CONDITION_TYPES;
export const defaultConditionApplyTypes = CONDITION_APPLY_TYPES;
